import React, {useState, useEffect, useMemo, useCallback} from 'react';
import { useBetween } from 'use-between';

let cachedProductContext = null;

function getProductContext() {
    if (cachedProductContext) {
        return cachedProductContext;
    }
    const productJsonElement = document.querySelector('[data-product-json]');
    if (!productJsonElement) {
        return null;
    }
    const jsonData = JSON.parse(productJsonElement.innerHTML);
    cachedProductContext = {
        shadeShape: jsonData.metafields['shade_shape'].toLowerCase(),
        title: jsonData.product.title,
    };
    return cachedProductContext;
}

const calculateShadeHeight = (width, height, shape) => {
    if (typeof width != 'number') {
        width = parseInt(width, 10);
    }
    if (typeof height != 'number') {
        height = parseInt(height, 10);
    }

    if (shape == 'hip') {
        return ((width * 0.189) + height).toFixed(2);
    } else if (shape == 'pyramid') {
        return (((width * 0.72) * 0.3047) + height).toFixed(2);
    } else if (shape == 'super_span') {
        return ((width * 0.209) + height).toFixed(2);
    } else if (shape == 'polygon') {
        return ((width * 0.3047) + height).toFixed(2);
    }
    return 0;
};

function parseTitle(title) {
    const entryHeightTitle = title.match(/\d+\sFoot Entry/);
    return entryHeightTitle != null ? parseInt(entryHeightTitle[0], 10) : null;
}

function getVariantDimensions(variant, shadeShape, title) {
    const titleEntryHeight = parseTitle(title);

    if (shadeShape == 'polygon') {
        return {
            width: variant.option1 == null ? 0 : parseInt(variant.option1, 10),
            entryHeight: variant.option2 == null ? 0 : parseInt(variant.option2, 10),
        };
    }
    if (shadeShape == 'hip' && titleEntryHeight != null) {
        return {
            width: variant.option2 == null ? 0 : parseInt(variant.option2, 10),
            entryHeight: titleEntryHeight,
        };
    }
    return {
        width: variant.option2 == null ? 0 : parseInt(variant.option2, 10),
        entryHeight: variant.option3 == null ? 0 : parseInt(variant.option3, 10),
    };
}

function computePeakHeight(width, entryHeight, shadeShape) {
    if (width > 0 && entryHeight > 0) {
        return calculateShadeHeight(width, entryHeight, shadeShape);
    }
    return 0;
}

export const shadeHeightComponent = () => {
    const productContext = getProductContext();
    const shadeShape = productContext?.shadeShape ?? '';
    const title = productContext?.title ?? '';

    const initialDimensions = getVariantDimensions(initialVariant, shadeShape, title);
    const [variantWidth, setVariantWidth] = useState(initialDimensions.width);
    const [variantEntryHeight, setVariantEntryHeight] = useState(initialDimensions.entryHeight);

    const finalVariantHeight = useMemo(
        () => computePeakHeight(variantWidth, variantEntryHeight, shadeShape),
        [variantWidth, variantEntryHeight, shadeShape]
    );

    const applyVariantDimensions = useCallback((newVariant) => {
        const { width, entryHeight } = getVariantDimensions(newVariant, shadeShape, title);
        setVariantWidth(width);
        setVariantEntryHeight(entryHeight);
    }, [shadeShape, title]);

    const updateVariant = useCallback((e) => {
        applyVariantDimensions(e.detail.variant);
    }, [applyVariantDimensions]);

    useEffect(() => {
        window.addEventListener('variant:changed', updateVariant);
        return () => {
            window.removeEventListener('variant:changed', updateVariant);
        };
    }, [updateVariant]);

    return {
        shadeShape,
        variantWidth,
        variantEntryHeight,
        finalVariantHeight,
    };
};

const useSharedShadeHeight = () => useBetween(shadeHeightComponent);

export const shadeHeightSpec = () => {
    const { finalVariantHeight } = useSharedShadeHeight();
    return (
        <>
            { finalVariantHeight > 0 ? finalVariantHeight : null } ft at peak
        </>
    );
};

export const shadeHeightCalculator = () => {
    const { shadeShape, variantWidth, variantEntryHeight } = useSharedShadeHeight();
    const [shadeWidth, setShadeWidth] = useState(variantWidth);
    const [shadeHeight, setShadeHeight] = useState(variantEntryHeight);

    useEffect(() => {
        setShadeWidth(variantWidth);
        setShadeHeight(variantEntryHeight);
    }, [variantWidth, variantEntryHeight]);

    const peakHeight = useMemo(
        () => computePeakHeight(shadeWidth, shadeHeight, shadeShape),
        [shadeWidth, shadeHeight, shadeShape]
    );

    const handleChange = (e) => {
        e.preventDefault();
        const { name, value } = e.target;

        if (name == 'shadeWidth') {
            setShadeWidth(value);
        } else if (name == 'shadeHeight') {
            setShadeHeight(value);
        }
    };

    return (
        <>
            <div id="shade-height-calc-form" className="form-wrapper calculator-wrapper">
                <p>Estimate overall height of the shade at it's peak.</p>

                <div className="form-inputs">
                    <div className='form-group length-width'>
                        <label className="control-label" htmlFor="shadeWidth">Width</label>
                        <div className="input-wrapper">
                            <input value={shadeWidth} onChange={handleChange} min={1} type="number"
                                name="shadeWidth" id="shadeWidth" placeholder="Enter the Width"
                                className="form-control input-md focus:ring-2"
                            />
                        </div>
                    </div>

                    <div className='form-group length-width'>
                        <label className="control-label" htmlFor="shadeHeight">Entry Height</label>
                        <div className="input-wrapper">
                            <input value={shadeHeight} onChange={handleChange} min={1} type="number"
                                name="shadeHeight" id="shadeHeight" placeholder="Enter the Entry Height"
                                className="form-control input-md focus:ring-2"
                            />
                        </div>
                    </div>
                </div>

                <div className="answer-container">
                    <p className="text-center text-base font-semibold heading h5">{ peakHeight } Feet at Peak</p>
                </div>
            </div>
        </>
    );
};
